import { useState } from "react";
import { Box, Button, Collapse, Typography } from "@mui/material";
import { ChevronDown, ChevronRight } from "lucide-react";
import LinkRouter from "components/navigation/LinkRouter";
import useIsMobile from "components/hooks/useIsMobile";

const SUPPORT_BANNER_REFS = new Set([
  "AAAAAAAAAAM=", // Jackie
  "AAAAAAAAAAI=", // Emily
  "AAAAAAAAF4E=", // Wilson
  "AAAAAAAAAAE=", // Kip
  "AAAAAAAAAVQ=", // Kevin
  "AAAAAAAAARM=", // Loren
]);

const STALE_HOURS = 36;

export type SupportLink = {
  title: string;
  description: string;
  href: string;
  targetNew?: boolean;
};

// Broad defaults cover day-to-day operations, client-service FAQs, and a clear
// escalation path. They are used when the DM analysis does not show a resource
// that is meaningfully more relevant for a particular support teammate.
export const COMMON_SUPPORT_LINKS: SupportLink[] = [
  {
    title: "Working with Faye",
    description: "A practical overview of day-to-day work on Faye.",
    href: "https://advisor.findfaye.com/community/AAAAAAAAE%2Fw%3D",
  },
  {
    title: "Common questions",
    description: "Answers to frequently asked client-service questions.",
    href: "https://advisor.findfaye.com/community/AAAAAAAAE5k%3D",
  },
  {
    title: "Report bug",
    description: "Let the team know when something isn't working.",
    href: "/forms/complete/c83717bd-77ae-41dd-a811-bbccb4841dbf",
    targetNew: true,
  },
];

export const SUPPORT_LINKS_BY_USER_REF: Partial<Record<string, SupportLink[]>> =
  {
    // Claude DM Analysis.xlsx: 62 of Jackie's 91 asks were about handoffs,
    // billing, or client accounts. These links provide direct paths for the
    // highest-volume actions instead of sending advisors back into a DM.
    "AAAAAAAAAAM=": [
      {
        title: "Family rematch request",
        description: "Request a family transition or advisor rematch.",
        href: "/tools/family-rematch",
      },
      {
        title: "Family billing changes",
        description: "Steps for plan, payment, and billing updates.",
        href: "/documents/16fce48e-0ee6-44bc-b4d6-8469ca714d99",
        targetNew: true,
      },
      {
        title: "Managing clients' hours",
        description: "Guidance for usage, overages, and plan hours.",
        href: "https://advisor.findfaye.com/community/AAAAAAAAIio%3D",
      },
    ],

    // Emily's asks were mixed, but billing was the largest category (4 of 15)
    // and several others concerned advertising, attribution, or lead growth.
    "AAAAAAAAAAI=": [
      {
        title: "Family billing changes",
        description: "Steps for plan, payment, and billing updates.",
        href: "/documents/16fce48e-0ee6-44bc-b4d6-8469ca714d99",
        targetNew: true,
      },
      {
        title: "Marketing templates",
        description: "Ready-to-use materials for advisor marketing.",
        href: "https://advisor.findfaye.com/community/AAAAAAAACMc%3D",
      },
      {
        title: "Growing your clientele",
        description: "Guidance for finding and converting new clients.",
        href: "https://advisor.findfaye.com/community/AAAAAAAAFMM%3D",
      },
    ],

    // Kip received 13 technical asks and 4 feature requests out of 24 total.
    // Bug, notification, and product-feedback links closely match that traffic.
    "AAAAAAAAAAE=": [
      {
        title: "Report bug",
        description: "Send the team details about a platform issue.",
        href: "/forms/complete/c83717bd-77ae-41dd-a811-bbccb4841dbf",
        targetNew: true,
      },
      {
        title: "Share an idea or feature request",
        description: "Submit product feedback directly to the team.",
        href: "/forms/complete/7a73c5fe-c347-4ea9-ad6b-76e4e9389d8b",
        targetNew: true,
      },
      {
        title: "Using calendars",
        description: "A video guide to calendars and events in Faye.",
        href: "https://www.loom.com/share/73c325c713a740e89525104b93584d24?sid=859bf3cb-756d-40b0-8df3-ff103de76af3",
      },
    ],

    // Kevin received technical questions in 9 of 11 asks, concentrated around
    // calendars, email, group messaging, and calls. Pair the bug form with the
    // two platform walkthroughs that cover those surfaces.
    "AAAAAAAAAVQ=": [
      {
        title: "Report bug",
        description: "Send the team details about a platform issue.",
        href: "/forms/complete/c83717bd-77ae-41dd-a811-bbccb4841dbf",
        targetNew: true,
      },
      {
        title: "Using calendars",
        description: "A video guide to calendars and events in Faye.",
        href: "https://www.loom.com/share/73c325c713a740e89525104b93584d24?sid=859bf3cb-756d-40b0-8df3-ff103de76af3",
      },
      {
        title: "Inbox and messaging",
        description: "A video guide to inbox and messaging workflows.",
        href: "https://www.loom.com/share/8a28e6ca7f5f4d63a05fecbd75c28592?sid=93e4f666-7274-4d38-8b2e-33aa25d51fa5",
      },
    ],

    // Three of Loren's five asks were onboarding how-tos, and another asked
    // about finding more clients. These are unusually specific and map cleanly
    // to the onboarding, lead-handling, and growth resources.
    "AAAAAAAAARM=": [
      {
        title: "Client onboarding deck",
        description: "The presentation to use when onboarding a new client.",
        href: "https://docs.google.com/presentation/d/1kHZynr8Oygt3MVLof6kFAXoknIHhi9gK4dWTzOk42I4/",
      },
      {
        title: "Handling new client leads",
        description: "Guidance for following up with prospective clients.",
        href: "https://advisor.findfaye.com/community/AAAAAAAAEKI%3D",
      },
      {
        title: "Growing your clientele",
        description: "Guidance for finding and converting new clients.",
        href: "https://advisor.findfaye.com/community/AAAAAAAAFMM%3D",
      },
    ],
    // Note(Kip): My best guess for wilson.
    "AAAAAAAAF4E=": [
      {
        title: "Family billing changes",
        description: "Steps for plan, payment, and billing updates.",
        href: "/documents/16fce48e-0ee6-44bc-b4d6-8469ca714d99",
        targetNew: true,
      },
      {
        title: "Family Invoices",
        description:
          "View invoice history and payout details for your families.",
        href: "/tools/family-invoices",
      },
      {
        title: "Platform Fee FAQs",
        description: "Explanation of platform fees and edge cases.",
        href: "/documents/385b2f53-e6e3-4bf6-9208-cbf4ec256b39",
      },
    ],
  };

export const getSupportLinksForUsers = (
  userRefs: string[],
  linksByUserRef = SUPPORT_LINKS_BY_USER_REF,
): SupportLink[] => {
  for (const userRef of userRefs) {
    const userLinks = linksByUserRef[userRef];
    if (userLinks) {
      return userLinks;
    }
  }
  return COMMON_SUPPORT_LINKS;
};

export const getSupportBannerParticipantRef = (
  participantRefs: string[],
): string | undefined =>
  participantRefs.find((ref) => SUPPORT_BANNER_REFS.has(ref));

export const shouldShowSupportBanner = (
  participantRefs: string[],
  lastMessageTimestampSec?: bigint,
): boolean => {
  if (!getSupportBannerParticipantRef(participantRefs)) {
    return false;
  }
  if (lastMessageTimestampSec) {
    const nowSec = Math.floor(Date.now() / 1000);
    const staleSec = STALE_HOURS * 60 * 60;
    if (nowSec - Number(lastMessageTimestampSec) < staleSec) {
      return false;
    }
  }
  return true;
};

type SupportBannerProps = {
  participantRefs: string[];
  supportName?: string;
  linksByUserRef?: Partial<Record<string, SupportLink[]>>;
};

const SupportLinkRow = ({ link }: { link: SupportLink }) => {
  const content = (
    <>
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Typography sx={{ fontSize: "15px", fontWeight: 700 }}>
          {link.title}
        </Typography>
        <Typography
          sx={{
            marginTop: "1px",
            fontSize: "13px",
            fontWeight: 400,
            lineHeight: 1.4,
            color: "#6B6E7B",
          }}
        >
          {link.description}
        </Typography>
      </Box>
      <ChevronRight size={18} color="#198282" aria-hidden="true" />
    </>
  );
  const sx = {
    padding: { xs: "9px 16px", sm: "9px 20px" },
    display: "flex",
    alignItems: "center",
    gap: "12px",
    color: "#3D3D3D",
    textDecoration: "none",
    cursor: "pointer",
    "&:hover": { backgroundColor: "#F2F8F8" },
  };

  if (/^https?:\/\//.test(link.href)) {
    return (
      <Box
        component="a"
        href={link.href}
        target={link.targetNew ? "_blank" : undefined}
        rel={link.targetNew ? "noopener noreferrer" : undefined}
        sx={sx}
      >
        {content}
      </Box>
    );
  }

  return (
    <Box
      component={LinkRouter}
      to={link.href}
      targetNew={link.targetNew}
      sx={sx}
    >
      {content}
    </Box>
  );
};

export default ({
  participantRefs,
  supportName,
  linksByUserRef = SUPPORT_LINKS_BY_USER_REF,
}: SupportBannerProps) => {
  const isMobile = useIsMobile();
  const [expanded, setExpanded] = useState(false);
  const supportLinks = getSupportLinksForUsers(participantRefs, linksByUserRef);
  const supportEmailLink = `/inbox/email/create?email=${encodeURIComponent(
    "support@findfaye.com",
  )}`;

  return (
    <Box
      component="aside"
      sx={{
        margin: "12px 0",
        backgroundColor: "#F7FAFA",
        border: "1px solid #C9E0E0",
        borderRadius: "12px",
        boxShadow: "0 2px 8px rgba(61, 61, 61, 0.06)",
        overflow: "hidden",
      }}
    >
      <Box
        sx={{
          padding: { xs: "14px 16px", sm: "16px 20px" },
          display: "flex",
          alignItems: "center",
          flexWrap: "wrap",
          gap: "12px 20px",
        }}
      >
        <Box sx={{ minWidth: "260px", flex: 1 }}>
          <Typography
            sx={{
              fontSize: "16px",
              fontWeight: 700,
              lineHeight: 1.3,
              letterSpacing: "-0.2px",
              color: "#3D3D3D",
            }}
          >
            {supportName
              ? `${supportName} will not be available right away.`
              : "Your contact will not be available right away."}
          </Typography>
          <Typography
            component="div"
            sx={{
              marginTop: "3px",
              fontSize: "14px",
              fontWeight: 400,
              lineHeight: 1.45,
              color: "#6B6E7B",
            }}
          >
            For questions/concerns, please email{" "}
            <LinkRouter to={supportEmailLink} useColorScheme inline>
              support@findfaye.com
            </LinkRouter>{" "}
            so someone on the team can help.
          </Typography>
        </Box>
        <Box
          display="flex"
          flexDirection="row"
          width={isMobile ? "100%" : "auto"}
          justifyContent={isMobile ? "end" : undefined}
        >
          <Button
            variant="text"
            aria-expanded={expanded}
            aria-controls="common-support-links"
            onClick={() => setExpanded((current) => !current)}
            endIcon={
              <ChevronDown
                size={17}
                style={{
                  transform: expanded ? "rotate(180deg)" : "rotate(0deg)",
                  transition: "transform 180ms ease",
                }}
              />
            }
            sx={{
              fontSize: "14px",
              height: "unset",
            }}
          >
            Common help topics
          </Button>
        </Box>
      </Box>

      <Collapse in={expanded} timeout={200}>
        <Box
          id="common-support-links"
          sx={{
            backgroundColor: "rgba(255, 255, 255, 0.72)",
            borderTop: "1px solid #D6E7E7",
          }}
        >
          <Box sx={{ display: "flex", flexDirection: "column", gap: "4px" }}>
            {supportLinks.map((link) => (
              <SupportLinkRow key={link.href} link={link} />
            ))}
          </Box>
        </Box>
      </Collapse>
    </Box>
  );
};
